/**
 * Input/output contracts for the session bulk callable.
 *
 * Single-session creation goes through direct Firestore writes (see
 * `apps/admin/src/services/sessionService.ts`) — that path doesn't need
 * server-side label resolution. The bulk path lives here because every
 * row references `sessionTypeName` (resolves against
 * `event.config.sessionTypes[]`), `locationLabel` (resolves against
 * `eventLocations`), `audience` (resolves against named audiences OR a
 * personal audience by email), and `speakerEmails[]` (resolves via
 * `userProfiles` + active eventMembership). Doing those lookups
 * client-side would mean N round-trips per import; the callable does them
 * once for the whole batch.
 *
 * Audience targeting (ADR-120, single-cell variant): each row's
 * `audience` field is one string — either a named-audience name OR an
 * email address (server detects `@`). It resolves to exactly one
 * `audienceId` per session: a named audience match by name, or that
 * user's personal audience by email. Multi-target rows go through the
 * admin compose form, not the workbook — keeping imports to one
 * audience per row avoids needing anonymous-composition logic in the
 * import path. Empty audience cells fall back to the row's category
 * `defaultAudienceId` if set; otherwise the row fails with
 * `audience_required`.
 *
 * Timestamps are converted from event-local strings to UTC admin-side
 * (`apps/admin/src/utils/timestamps.ts:dateTimeLocalToTimestamp`) before
 * being sent — the dialog has the event's timezone via `useEvent`. The
 * server receives ISO datetime strings and constructs Firestore
 * Timestamps.
 */

import { z } from 'zod';

export const bulkCreateSessionsRowSchema = z
  .object({
    title: z.string().min(1).max(500),
    description: z.string().nullable(),
    /** Resolved server-side against `event.config.sessionTypes[i].name`. */
    sessionTypeName: z.string().min(1),
    /** ISO-8601 datetime string (UTC), e.g. "2026-02-13T19:00:00.000Z". */
    startTime: z.string().min(1),
    endTime: z.string().min(1),
    /** Resolved server-side against `eventLocations.label` for this event. Null = unlocated. */
    locationLabel: z.string().nullable(),
    /**
     * Single audience for the row — a named audience name or an email.
     * Server detects `@`: contains-`@` resolves to a personal audience
     * via userProfiles + active eventMembership, otherwise resolves to a
     * named audience (custom group or role audience) by case-insensitive
     * name match. Null = use the row's category defaultAudienceId.
     */
    audience: z.string().min(1).nullable(),
    /**
     * Speakers are personIds; the server resolves each speakerEmail to
     * a personId (claimed or pending) and pushes onto
     * session.speakerIds[] in row order (ADR-136 amendment).
     */
    speakerEmails: z.array(z.string().email()),
  })
  .strict();

export type BulkCreateSessionsRow = z.infer<typeof bulkCreateSessionsRowSchema>;

export const bulkCreateSessionsInputSchema = z
  .object({
    eventId: z.string().min(1),
    rows: z.array(bulkCreateSessionsRowSchema).min(1).max(500),
  })
  .strict();

export type BulkCreateSessionsInput = z.infer<
  typeof bulkCreateSessionsInputSchema
>;

export type BulkCreateSessionsRowStatus =
  | 'created'
  | 'duplicate_title' // existing session in this event has the same title + start time
  | 'unknown_category' // sessionTypeName doesn't match any event.config.sessionTypes entry
  | 'unknown_location' // locationLabel set but no matching eventLocations row
  | 'unknown_audience' // audience cell didn't resolve (name not found, or email not on roster)
  | 'audience_required' // audience cell empty AND row's category has no defaultAudienceId
  | 'unresolved_emails' // a speakerEmail didn't resolve to an active eventMembership
  | 'invalid_time' // startTime / endTime parse failure or end <= start
  | 'failed';

export interface BulkCreateSessionsRowResult {
  index: number;
  title: string;
  status: BulkCreateSessionsRowStatus;
  /** New session document ID, populated when status === 'created'. */
  sessionId?: string;
  /** Counts populated when status === 'created'. */
  resolvedSpeakers?: number;
  /** Populated when status === 'unresolved_emails' or 'unknown_audience'. */
  unresolvedNames?: string[];
  /** Populated for any non-success status. */
  message?: string;
}

export interface BulkCreateSessionsResponse {
  success: true;
  results: BulkCreateSessionsRowResult[];
  summary: {
    created: number;
    duplicateTitle: number;
    unknownCategory: number;
    unknownLocation: number;
    unknownAudience: number;
    audienceRequired: number;
    unresolvedEmails: number;
    invalidTime: number;
    failed: number;
  };
}

// ---------------------------------------------------------------------------
// bulkResolveSessions — Phase C of the resolve-and-propose architecture.
//
// Same input as bulkCreateSessions (eventId + rows), but the callable does
// NOT write. Returns per-row + per-cell proposals so the admin dialog can
// show "this row maps to category X / location Y / audience Z; speakers
// resolved to A, B (existing users in Q3 Summit)" before any database
// mutation. Surfaces ADR-057 identity matches for every email cell so
// duplicate-profile risk is visible.
// ---------------------------------------------------------------------------

export const bulkResolveSessionsInputSchema = bulkCreateSessionsInputSchema;

export type BulkResolveSessionsInput = z.infer<
  typeof bulkResolveSessionsInputSchema
>;

export type BulkResolveSessionsRowStatus =
  | 'new'
  | 'duplicate_title'
  | 'unknown_category'
  | 'unknown_location'
  | 'unknown_audience'
  | 'audience_required'
  | 'unresolved_speakers'
  | 'invalid_time'
  | 'failed';

/**
 * Per-cell proposals captured during resolve. Surfaced to the dialog so
 * it can show what the row would write if accepted, even when status is
 * not 'new' (e.g. unknown_audience rows still surface the location and
 * speakers proposals so the organizer can correct the audience cell with
 * full context).
 */
export interface BulkResolveSessionsProposal {
  /** Resolved category (sessionTypeName -> sessionTypeId), if matched. */
  sessionType?: { id: string; name: string };
  /** Resolved location, if locationLabel was provided and matched. */
  location?: { id: string; label: string };
  /**
   * Resolved audience target. For empty audience cells with a category
   * default, source='category_default'. For email cells matching a person
   * on this event roster, source='personal'. For named cells, source='named'.
   * Omitted when the cell did not resolve.
   */
  audience?:
    | { source: 'named'; audienceId: string; name: string }
    | {
        source: 'personal';
        audienceId: string;
        userId?: string;
        contactId?: string;
        displayName: string;
        photoURL?: string | null;
        priorEventNames?: string[];
        pending?: boolean;
      }
    | { source: 'category_default'; audienceId: string };
  /**
   * Per-speaker resolution. Always populated (one entry per speakerEmail
   * in the row); the entry's `source` indicates the lookup outcome. UI
   * renders person chips and surfaces ADR-057 prior-event affiliations.
   */
  speakerProposals: BulkResolveSessionsSpeakerProposal[];
}

export type BulkResolveSessionsSpeakerProposal =
  | {
      email: string;
      source: 'roster_member';
      personId: string;
      displayName: string;
      photoURL: string | null;
      priorEventNames: string[];
      pending: boolean;
    }
  | {
      email: string;
      source: 'not_on_roster';
      personId: string;
      displayName: string;
      photoURL: string | null;
      priorEventNames: string[];
    }
  | { email: string; source: 'no_match' };

export interface BulkResolveSessionsRowResult {
  index: number;
  title: string;
  status: BulkResolveSessionsRowStatus;
  /** Populated for every non-'failed' status, including unresolved ones. */
  proposal?: BulkResolveSessionsProposal;
  /** Populated when status indicates an unresolved cell. */
  unresolvedNames?: string[];
  /** Populated for any non-'new' status. */
  message?: string;
}

export interface BulkResolveSessionsResponse {
  success: true;
  results: BulkResolveSessionsRowResult[];
  summary: {
    new: number;
    duplicateTitle: number;
    unknownCategory: number;
    unknownLocation: number;
    unknownAudience: number;
    audienceRequired: number;
    unresolvedSpeakers: number;
    invalidTime: number;
    failed: number;
  };
}
